import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { parseRawEmail } from "../../../utils/parseRawEmail";
import { parseCustomerEmail, plainEmailText } from "../emailCompose";
import ImageMessage, { resolveChatImageUrl } from "../ImageMessage";

const extractStructuredHints = (text) => {
  if (!text) return [];
  const hints = [];
  const patterns = [
    { key: "pedido", re: /(?:pedido|order)\s*(?:n[ºo°.]?|number)?\s*:?\s*([^\n,]+)/i },
    { key: "telefone", re: /(?:telefone|phone|celular)\s*:?\s*([^\n,]+)/i },
    { key: "valor", re: /(?:valor|total)\s*:?\s*([^\n]+)/i },
    { key: "cliente", re: /(?:cliente|customer)\s*:?\s*([^\n,]+)/i },
  ];
  patterns.forEach(({ key, re }) => {
    const m = String(text).match(re);
    if (m?.[1]) hints.push({ key, value: m[1].trim() });
  });
  return hints;
};

const AddressRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <Box sx={{ display: "flex", gap: 0.75, alignItems: "baseline", minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", fontWeight: 600, flexShrink: 0, fontSize: "0.72rem" }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: "text.primary",
          fontSize: "0.72rem",
          wordBreak: "break-word",
          minWidth: 0,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const EmailAttachmentStrip = ({ attachments, conversationImageGallery }) => {
  const theme = useTheme();
  if (!Array.isArray(attachments) || attachments.length === 0) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, px: 1.5, pt: 0.5, pb: 1.25 }}>
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", fontWeight: 700, fontSize: "0.68rem", letterSpacing: 0.3 }}
      >
        {attachments.length === 1 ? "1 anexo" : `${attachments.length} anexos`}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "flex-start" }}>
        {attachments.map((file, index) => {
          const href = file.url ? resolveChatImageUrl(String(file.url)) : "";
          const label = file.name || "Anexo";
          if (file.kind === "image" && (file.sourceMessage?.image || href)) {
            const galleryIndex = Array.isArray(conversationImageGallery)
              ? Math.max(
                  0,
                  conversationImageGallery.findIndex(
                    (u) => u && href && resolveChatImageUrl(u) === resolveChatImageUrl(href)
                  )
                )
              : 0;
            return (
              <Box
                key={file.id || `${label}-${index}`}
                sx={{
                  maxWidth: "100%",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  overflow: "hidden",
                  bgcolor: theme.palette.background.paper,
                }}
              >
                <ImageMessage
                  message={file.sourceMessage || { image: href, type: "image" }}
                  galleryUrls={conversationImageGallery}
                  galleryIndex={galleryIndex}
                />
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    px: 0.75,
                    py: 0.4,
                    fontWeight: 600,
                    fontSize: "0.65rem",
                    wordBreak: "break-word",
                  }}
                >
                  {label}
                </Typography>
              </Box>
            );
          }
          return (
            <Box
              key={file.id || `${label}-${index}`}
              component={href ? "a" : "div"}
              href={href || undefined}
              target={href ? "_blank" : undefined}
              rel={href ? "noopener noreferrer" : undefined}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                minWidth: 0,
                maxWidth: "100%",
                color: "text.primary",
                textDecoration: "none",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                px: 1,
                py: 0.5,
                "&:hover": href ? { bgcolor: alpha(theme.palette.primary.main, 0.06) } : undefined,
              }}
            >
              <AttachFileIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="caption" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/**
 * Card de e-mail recebido. Espelha o card de envio (assunto, anexos, ação
 * explícita) sem o rodapé legado "clique no ícone de e-mail".
 */
const EmailMessageCard = ({
  message,
  children,
  attachments,
  hasOriginal = false,
  onOpenOriginal,
  conversationImageGallery,
}) => {
  const theme = useTheme();
  const inbound = useMemo(() => parseCustomerEmail(message), [message]);
  const mime = useMemo(
    () => parseRawEmail(message?.emailOriginal || ""),
    [message?.emailOriginal]
  );
  const subject =
    inbound.subject || mime.friendlyHeaders?.subject || message?.emailSubject || "";
  const from =
    mime.friendlyHeaders?.from ||
    message?.emailFrom ||
    (message?.email_retorno && !/^(bounced|failed|rejected)/i.test(String(message.email_retorno))
      ? message.email_retorno
      : "");
  const preview = plainEmailText(inbound.body || "").replace(/\s+/g, " ").trim();
  const hints = useMemo(
    () => extractStructuredHints(inbound.body || message?.text || ""),
    [inbound.body, message?.text]
  );
  const files = Array.isArray(attachments)
    ? attachments
    : Array.isArray(message?.emailAttachments)
      ? message.emailAttachments
      : [];
  const [expanded, setExpanded] = useState(() => preview.length > 0 && preview.length < 180);
  const accent = theme.palette.primary.main;

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderLeft: `3px solid ${accent}`,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: theme.palette.background.paper,
        color: theme.palette.text.primary,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          px: 1.5,
          py: 1.25,
          bgcolor: theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[50],
        }}
      >
        <MailOutlineIcon sx={{ fontSize: 20, mt: 0.25, color: accent }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "0.68rem",
                textTransform: "uppercase",
                letterSpacing: 0.4,
                fontWeight: 700,
              }}
            >
              E-mail recebido
            </Typography>
          </Box>
          {subject ? (
            <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.25, mb: 0.5 }}>
              {subject}
            </Typography>
          ) : null}
          <AddressRow label="De:" value={from} />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0 }}>
          {hasOriginal && typeof onOpenOriginal === "function" ? (
            <Tooltip title="Abrir e-mail original na íntegra">
              <Button
                size="small"
                variant="text"
                onClick={onOpenOriginal}
                startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  minWidth: 0,
                  px: 0.75,
                  whiteSpace: "nowrap",
                }}
              >
                Ver original
              </Button>
            </Tooltip>
          ) : null}
          <Tooltip title={expanded ? "Recolher e-mail" : "Ver e-mail completo"}>
            <IconButton size="small" onClick={() => setExpanded((v) => !v)} aria-label="Expandir e-mail">
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {hints.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ px: 1.5, py: 1 }}>
          {hints.map((h) => (
            <Chip
              key={h.key}
              size="small"
              label={`${h.key}: ${h.value}`}
              variant="outlined"
              sx={{ height: 24, fontSize: "0.72rem" }}
            />
          ))}
        </Stack>
      )}

      <Collapse in={expanded}>
        <Box sx={{ px: 1.5, py: 1.25, borderTop: `1px solid ${theme.palette.divider}` }}>
          {children}
        </Box>
      </Collapse>

      {!expanded && preview ? (
        <Box sx={{ px: 1.5, pt: 1, pb: files.length ? 0.5 : 1.25 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              fontSize: "0.85rem",
              lineHeight: 1.45,
              maxHeight: "calc(0.85rem * 1.45 * 3)",
            }}
          >
            {preview.slice(0, 280)}
          </Typography>
        </Box>
      ) : null}

      <EmailAttachmentStrip
        attachments={files}
        conversationImageGallery={conversationImageGallery}
      />
    </Box>
  );
};

export default EmailMessageCard;
